import { makeAutoObservable, runInAction } from 'mobx'
import { debounce } from 'lodash'
import { isCancel } from 'axios'
import { logger } from 'shared/lib'
import { RequestCancelAxios } from 'shared/api/lib'
import { showToast } from 'shared/ui'
import { type IRequestReview, ReviewsApi } from 'entities/Review'

export class RequestReviewStore {
  private _reviews: IRequestReview[] = []
  private _search = ''
  private _loading = false
  private _error = false
  private _requestCancelAxios = new RequestCancelAxios()
  private readonly _debouncedLoad: ReturnType<typeof debounce>

  constructor() {
    makeAutoObservable(this)
    this._debouncedLoad = debounce(this.loadReviews, 500)
    void this.loadReviews()
  }

  get reviews() {
    return this._reviews
  }

  get search() {
    return this._search
  }

  get loading() {
    return this._loading
  }

  get error() {
    return this._error
  }

  get params() {
    if (!this._search) return

    return {
      query: this._search.trim(),
    }
  }

  setSearch = (search: string) => {
    this._requestCancelAxios.reset()
    this._debouncedLoad?.cancel?.()
    this._search = search
    this._loading = true
    this._error = false
    this._reviews = []
    this._debouncedLoad()
  }

  loadReviews = async () => {
    try {
      this._requestCancelAxios.init()

      runInAction(() => {
        this._loading = true
        this._error = false
      })

      const {
        data: { data },
      } = await ReviewsApi.getRequestReviewsAll(this.params, {
        cancelToken: this._requestCancelAxios.token,
      })

      runInAction(() => {
        this._reviews = Array.isArray(data) ? data : []
        this._loading = false
      })
    } catch (e) {
      if (!isCancel(e)) {
        logger.error(e)

        showToast({
          type: 'error',
          title: 'Something went wrong. Please try again',
        })

        runInAction(() => {
          this._reviews = []
          this._loading = false
          this._error = true
        })
      }
    }
  }

  dispose = () => {
    this._requestCancelAxios.reset()
    this._debouncedLoad?.cancel?.()
  }
}
